/**
 * Street Address Autocomplete
 *
 * A plain text input that, after three characters, offers real US street addresses
 * from Google Places through our API (`/api/locations/addresses/*`). Picking one fills
 * the street line and hands the parent the city/state so it can fill those too.
 *
 * It stays a free-text field throughout: typing without picking is fine, and if the
 * API has no key (`available: false`) or a lookup fails, the dropdown simply never
 * shows. One session token covers a typing session plus the pick, which is how Google
 * bills it as a session rather than per keystroke; a new token is minted after each pick.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export interface AddressSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

export interface SelectedAddress {
  street: string;
  city: string | null;
  stateCode: string | null;
  zip: string | null;
  formatted: string;
  /** City/state as Trade Loop's dataset spells it, or null when it could not be matched. */
  location: { city: string; stateCode: string; stateName: string; formatted: string } | null;
}

interface StreetAddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: SelectedAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

function newSessionToken(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export default function StreetAddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "123 Main Street",
  disabled = false,
  className = "",
}: StreetAddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // Flipped off for the rest of the page when the API says no key is configured.
  const [available, setAvailable] = useState(true);
  const sessionToken = useRef<string | null>(null);
  // Set when we wrote the value ourselves (a pick), so the next change does not re-search it.
  const skipNextSearch = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced lookup as the user types
  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return;
    }
    const query = value.trim();
    if (!available || query.length < MIN_CHARS) {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        if (!sessionToken.current) sessionToken.current = newSessionToken();
        const params: Record<string, string> = { q: query, session: sessionToken.current };
        const response = await api.get<{ available: boolean; data: AddressSuggestion[] }>(
          "/api/locations/addresses/suggest",
          { params }
        );
        if (cancelled) return;
        if (response?.available === false) {
          setAvailable(false);
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
        const results = Array.isArray(response?.data) ? response.data : [];
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setHighlightedIndex(-1);
      } catch (error) {
        // Suggestions are a convenience; the field still works as plain text.
        console.warn("[StreetAddressAutocomplete] Lookup failed:", error);
        if (!cancelled) {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, available]);

  const handleSelect = useCallback(
    async (suggestion: AddressSuggestion) => {
      setSuggestions([]);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      skipNextSearch.current = true;
      onChange(suggestion.mainText);

      const session = sessionToken.current ?? newSessionToken();
      // The pick ends this billing session; the next keystroke starts a fresh one.
      sessionToken.current = null;
      setIsResolving(true);
      try {
        const response = await api.get<{ data: SelectedAddress }>(
          `/api/locations/addresses/${encodeURIComponent(suggestion.placeId)}`,
          { params: { session } }
        );
        if (response?.data?.street) {
          skipNextSearch.current = true;
          onChange(response.data.street);
          onSelect(response.data);
        }
      } catch (error) {
        // The street line from the suggestion is already in the box; the user picks the city by hand.
        console.warn("[StreetAddressAutocomplete] Could not resolve the address:", error);
      } finally {
        setIsResolving(false);
      }
    },
    [onChange, onSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case "Enter":
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          e.preventDefault();
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
      case "Tab":
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <div className="relative">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="pr-10"
          aria-expanded={showSuggestions}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          role="combobox"
        />
        {(isSearching || isResolving) && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div ref={listRef} className="max-h-60 overflow-y-auto" role="listbox">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.placeId}
                type="button"
                role="option"
                aria-selected={index === highlightedIndex}
                className={cn(
                  "w-full px-4 py-3 text-left focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors",
                  index === highlightedIndex ? "bg-blue-50" : "hover:bg-gray-50"
                )}
                onClick={() => handleSelect(suggestion)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 truncate">{suggestion.mainText}</div>
                    <div className="text-sm text-gray-500 truncate">{suggestion.secondaryText}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {/* Google's terms ask for attribution wherever its suggestions appear without a map */}
          <div className="px-4 py-1.5 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-400 text-right">
            Suggestions by Google
          </div>
        </div>
      )}
    </div>
  );
}

export { StreetAddressAutocomplete };
